import React, { useState, useEffect } from "react";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import DatePicker, { registerLocale } from "react-datepicker";
import { cs, enUS } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { init, getCSRFToken } from "@utilities";
import { Tabs, HorizontalScrollPanel } from "@modules";
import { ExchangeChartProps } from "../types";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export const options = {
  responsive: true,
  maintainAspectRatio: false,
  locale: "cs-CZ",
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label(context) {
          let label = context.dataset.label || "";

          if (label) {
            label += ": ";
          }

          if (context.parsed.y !== null) {
            label += Math.round(context.parsed.y * 1000) / 1000;
          }
          return label;
        },
      },
    },
  },
  scales: {
    x: { display: true },
    y: {
      offset: true,
    },
  },
  elements: {
    line: { borderWidth: 1.5 },
    point: {
      radius: 1.5,
    },
  },
};

const ExchangeChart: React.FC<ExchangeChartProps> = ({
  currencyIso,
  cultureCode,
  localization,
  onDataChange,
  queryDate,
}) => {
  const date = queryDate.slice(0, 10);
  const basicDate = new Date(date);
  const dateOneYearAgo = new Date(date);
  const initFrom = new Date(dateOneYearAgo.setFullYear(dateOneYearAgo.getFullYear() - 1));
  const today = new Date();

  const [dataArrayOne, setDataArrayOne] = useState([]);
  const [dataArrayTwo, setDataArrayTwo] = useState([]);
  const [selectedDateFrom, setSelectedDateFrom] = useState(initFrom);
  const [selectedDateTo, setSelectedDateTo] = useState(basicDate);
  const [selectedDateFromIso, setSelectedDateFromIso] = useState(
    initFrom.toISOString().slice(0, 10)
  );
  const [selectedDateToIso, setSelectedDateToIso] = useState(basicDate.toISOString().slice(0, 10));
  const [dateRangeOpened, setdateRangeOpened] = useState(false);
  const [customDateRangeTriggered, setCustomDateRangeTriggered] = useState(false);

  const fetchData = async (timeFrame, dateFrom?, dateTo?) => {
    const currentDate = queryDate ?? new Date().toJSON().slice(0, 10);
    const btnConfig = {
      dateFrom: dateFrom || currentDate,
      dateTo: dateTo || currentDate,
      cultureCode,
      timeFrame: timeFrame ?? 0,
      currencyIsoCode: currencyIso,
    };

    try {
      const rawResponse = await fetch(`${window.location.origin}/api/ExchangeRate/ChartData`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          __RequestVerificationToken: getCSRFToken(),
        },
        body: JSON.stringify(btnConfig),
      });

      const content = await rawResponse.json();

      const arrayOne = [];
      const arrayTwo = [];
      if (content.payload.data) {
        Object.keys(content.payload.data).forEach((key) => {
          arrayOne.push(Math.round(content.payload.data[key].value * 1000) / 1000);
          const rawDate = content.payload.data[key].date;
          const d = new Date(rawDate.replace(/-/g, "/"));
          const dateCZ = d.toLocaleDateString("cs-cz");
          arrayTwo.push(dateCZ);
        });
      }

      setDataArrayOne(arrayOne);
      setDataArrayTwo(arrayTwo);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Zde zobrazte chybovou zprávu uživateli nebo provedte další akce v případě chyby
    }
  };

  const handleClick = (timeFrame) => {
    let adjustedTimeFrame = timeFrame;
    if (customDateRangeTriggered && timeFrame === 12) {
      adjustedTimeFrame = 0;
    }

    let toDate = selectedDateToIso || date;
    let fromDate = selectedDateFromIso || date;

    if (!(timeFrame === 0 || timeFrame === 12)) {
      // reset link date to init
      toDate = basicDate.toJSON().slice(0, 10);
      fromDate = initFrom.toJSON().slice(0, 10);
      setdateRangeOpened(false);
    }

    const btnData = {
      fromDate: toDate,
      toDate: fromDate,
      timeFrame: adjustedTimeFrame,
    };

    if (customDateRangeTriggered && adjustedTimeFrame === 0) {
      fetchData(adjustedTimeFrame, toDate, fromDate);
    } else {
      fetchData(adjustedTimeFrame);
    }

    onDataChange(btnData);
  };

  // Registrace jazyka pro datepicker
  if (cultureCode.toLowerCase() === "en-us") {
    registerLocale("en", enUS);
  } else {
    registerLocale("cs", cs);
  }

  const handleDateChangeFrom = (input) => {
    setSelectedDateFrom(input);
    const rawDate = new Date(input);
    const formatedDate = rawDate.toISOString().slice(0, 10);
    setSelectedDateFromIso(formatedDate);
    fetchData(0, selectedDateToIso, formatedDate);
    const btnData = {
      fromDate: selectedDateToIso || date,
      toDate: formatedDate || date,
      timeFrame: 0,
    };
    onDataChange(btnData);
  };

  const handleDateChangeTo = (input) => {
    setSelectedDateTo(input);
    const rawDate = new Date(input);
    const formatedDate = rawDate.toISOString().slice(0, 10);
    setSelectedDateToIso(formatedDate);
    fetchData(0, formatedDate, selectedDateFromIso);
    const btnData = {
      fromDate: formatedDate || date,
      toDate: selectedDateFromIso || date,
      timeFrame: 0,
    };
    onDataChange(btnData);
  };

  const showDateRange = () => {
    setdateRangeOpened(true);
    setCustomDateRangeTriggered(true);
  };

  useEffect(() => {
    fetchData(1);
    init.multiple(Tabs, document.querySelectorAll(".js-tabs"));
    init.multiple(HorizontalScrollPanel, document.querySelectorAll(".js-horizontal-panel"));
  }, []);

  const data = {
    labels: dataArrayTwo,
    datasets: [
      {
        data: dataArrayOne,
        borderColor: "#E00000",
        pointBackgroundColor: "#E00000",
        pointBorderColor: "#E00000",
        backgroundColor: "#E00000",
        tension: 0.1,
        spanGaps: true,
      },
    ],
  };

  return (
    <div className="js-tabs mb-12 overflow-hidden">
      <h3 className="mb-6 break-words text-2xl">{localization.Chart_Title}</h3>
      <div className="-mt-3 sm:-mt-4">
        <div className="overflow-hidden">
          <div className="row">
            <div className="sm:col-24">
              <div className="horizontal-scroll__outer-container">
                <div className="js-horizontal-panel relative sm:flex sm:justify-center">
                  <ul
                    className="js-tabs-button-list js-horizontal-panel-button-list horizontal-scroll__panel-wrapper"
                    role="tablist"
                  >
                    <li className="horizontal-scroll__panel-item" data-testid="test-id">
                      <button
                        role="tab"
                        type="button"
                        className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button is-active"
                        draggable="false"
                        aria-selected="true"
                        onClick={() => handleClick(1)}
                      >
                        <span
                          className="text-xs text-grey-160 sm:text-base"
                          data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                        >
                          {localization.Chart_Button_1month}
                        </span>
                      </button>
                    </li>
                    <li className="horizontal-scroll__panel-item" data-testid="test-id">
                      <button
                        role="tab"
                        type="button"
                        className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button"
                        draggable="false"
                        aria-selected="false"
                        onClick={() => handleClick(3)}
                      >
                        <span
                          className="text-xs text-grey-160 sm:text-base"
                          data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                        >
                          {localization.Chart_Button_3months}
                        </span>
                      </button>
                    </li>
                    <li className="horizontal-scroll__panel-item" data-testid="test-id">
                      <button
                        role="tab"
                        type="button"
                        className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button"
                        draggable="false"
                        aria-selected="false"
                        onClick={() => handleClick(6)}
                      >
                        <span
                          className="text-xs text-grey-160 sm:text-base"
                          data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                        >
                          {localization.Chart_Button_6months}
                        </span>
                      </button>
                    </li>
                    <li className="horizontal-scroll__panel-item" data-testid="test-id">
                      <button
                        role="tab"
                        type="button"
                        className="js-tabs-button js-horizontal-panel-button horizontal-scroll__button tab__button tab__button"
                        draggable="false"
                        aria-selected="false"
                        onClick={() => {
                          handleClick(12);
                          showDateRange();
                        }}
                      >
                        <span
                          className="text-xs text-grey-160 sm:text-base"
                          data-testid="accordion-widget-tabs-title-id-adcb3555-721d-4f93-a47b-ad9097ee81ba"
                        >
                          {localization.Chart_Button_custom}
                        </span>
                      </button>
                    </li>
                  </ul>
                  <button
                    className="js-horizontal-panel-prev-button horizontal-scroll__button-arrow horizontal-scroll__button-prev hidden"
                    type="button"
                    aria-label="Předchozí"
                    tabIndex={-1}
                    data-testid="tabs-widget-tabs-scroll-btn-id"
                  >
                    <svg
                      name="arrow-right"
                      className="icon h-8 w-8 shrink-0 rotate-180 fill-red-corporate"
                      role="img"
                      viewBox="0 0 32 32"
                    >
                      <path d="M17.061 16L12.111 11.05L13.525 9.63599L19.889 16L13.525 22.364L12.111 20.95L17.061 16Z" />
                    </svg>
                  </button>
                  <button
                    className="js-horizontal-panel-next-button horizontal-scroll__button-arrow horizontal-scroll__button-next hidden"
                    type="button"
                    aria-label="Další"
                    tabIndex={-1}
                    data-testid="tabs-widget-tabs-scroll-btn-id"
                  >
                    <svg
                      name="arrow-right"
                      className="icon h-8 w-8 shrink-0 fill-red-corporate"
                      role="img"
                      viewBox="0 0 32 32"
                    >
                      <path d="M17.061 16L12.111 11.05L13.525 9.63599L19.889 16L13.525 22.364L12.111 20.95L17.061 16Z" />
                    </svg>
                  </button>
                </div>
              </div>
              <div
                className={`${
                  dateRangeOpened ? "flex" : "hidden"
                } flex-wrap items-center justify-center sm:flex-nowrap`}
              >
                {/* datepicker */}
                <div className="mb-8 w-full sm:mb-3 sm:mr-8 sm:w-auto">
                  <label className="block max-w-xxs" htmlFor="datepickerFrom">
                    <span className="mb-1.5 block text-2xs">{localization.Chart_Button_from}</span>
                  </label>
                  <DatePicker
                    id="datepickerFrom"
                    selected={selectedDateFrom}
                    className="h-12 rounded-xs border border-grey-30 !px-3 py-2.5 text-base outline-grey-80"
                    onChange={(input) => {
                      handleDateChangeFrom(input);
                    }}
                    locale="cs"
                    dateFormat="dd.MM.yyyy"
                    maxDate={today} // Add this line
                    showIcon
                    toggleCalendarOnIconClick
                    icon={
                      <svg
                        className="!w-8 !h-8 !right-0"
                        width="32"
                        height="32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.75 5.25v4.5h-1.5v-4.5h1.5Zm8 0v4.5h-1.5v-4.5h1.5Zm-14 7.84a.75.75 0 0 1 .75-.75h17a.75.75 0 0 1 0 1.5h-17a.75.75 0 0 1-.75-.75Z"
                          fill="#E00000"
                        />
                        <path
                          d="M8.737 9.385c-.64.69-.987 1.73-.987 3.115V21c0 1.385.346 2.425.987 3.115.631.68 1.656 1.135 3.263 1.135h8c1.607 0 2.631-.455 3.263-1.135.64-.69.987-1.73.987-3.115v-8.5c0-1.385-.346-2.425-.987-3.115C22.63 8.705 21.607 8.25 20 8.25h-8c-1.607 0-2.632.455-3.263 1.135Zm-1.1-1.02C8.633 7.295 10.108 6.75 12 6.75h8c1.893 0 3.369.545 4.362 1.615.984 1.06 1.388 2.52 1.388 4.135V21c0 1.615-.404 3.075-1.388 4.135-.993 1.07-2.469 1.615-4.362 1.615h-8c-1.893 0-3.368-.545-4.362-1.615-.984-1.06-1.388-2.52-1.388-4.135v-8.5c0-1.615.404-3.075 1.388-4.135Z"
                          fill="#E00000"
                        />
                      </svg>
                    }
                  />
                </div>
                <div className="mb-8 w-full sm:mb-3 sm:mr-8 sm:w-auto">
                  <label className="block max-w-xxs" htmlFor="datepickerTo">
                    <span className="mb-1.5 block text-2xs">{localization.Chart_Button_to}</span>
                  </label>
                  <DatePicker
                    id="datepickerTo"
                    selected={selectedDateTo}
                    className="h-12 rounded-xs border border-grey-30 !px-3 py-2.5 text-base outline-grey-80"
                    onChange={(input) => {
                      handleDateChangeTo(input);
                    }}
                    locale="cs"
                    dateFormat="dd.MM.yyyy"
                    maxDate={today} // Add this line
                    showIcon
                    toggleCalendarOnIconClick
                    icon={
                      <svg
                        className="!w-8 !h-8 !right-0"
                        width="32"
                        height="32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.75 5.25v4.5h-1.5v-4.5h1.5Zm8 0v4.5h-1.5v-4.5h1.5Zm-14 7.84a.75.75 0 0 1 .75-.75h17a.75.75 0 0 1 0 1.5h-17a.75.75 0 0 1-.75-.75Z"
                          fill="#E00000"
                        />
                        <path
                          d="M8.737 9.385c-.64.69-.987 1.73-.987 3.115V21c0 1.385.346 2.425.987 3.115.631.68 1.656 1.135 3.263 1.135h8c1.607 0 2.631-.455 3.263-1.135.64-.69.987-1.73.987-3.115v-8.5c0-1.385-.346-2.425-.987-3.115C22.63 8.705 21.607 8.25 20 8.25h-8c-1.607 0-2.632.455-3.263 1.135Zm-1.1-1.02C8.633 7.295 10.108 6.75 12 6.75h8c1.893 0 3.369.545 4.362 1.615.984 1.06 1.388 2.52 1.388 4.135V21c0 1.615-.404 3.075-1.388 4.135-.993 1.07-2.469 1.615-4.362 1.615h-8c-1.893 0-3.368-.545-4.362-1.615-.984-1.06-1.388-2.52-1.388-4.135v-8.5c0-1.615.404-3.075 1.388-4.135Z"
                          fill="#E00000"
                        />
                      </svg>
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="exchangeChart">
        <Line options={options} data={data} />
      </div>
    </div>
  );
};
export default ExchangeChart;
